// Super Master > Modelos 3D (pedido 2026-11-04: "deixa uma aba para eu
// colocar as imagens glb lá para você conseguir pegar e criar os ícones").
import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Box, Download, Trash2, Upload } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MasterShell } from "@/components/master-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import { gpsDriveIconModelsService, type IconModelEntry } from "@/services/gpsdrive-icon-models.service";

export const Route = createFileRoute("/master/icon-models")({
  component: () => (
    <MasterShell>
      <IconModelsPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Modelos 3D — Super Master — GPS Drive" }] }),
});

const KEY = ["platform", "icon-models"] as const;

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function IconModelsPage() {
  const qc = useQueryClient();
  const { data: models, isLoading } = useQuery({ queryKey: KEY, queryFn: () => gpsDriveIconModelsService.list(), retry: false });
  const upload = useMutation({
    mutationFn: (v: { name: string; notes: string; file: File }) => gpsDriveIconModelsService.upload(v.name, v.notes, v.file),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => gpsDriveIconModelsService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<IconModelEntry | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = () => {
    if (!name.trim()) {
      setError("Informe o nome do modelo antes de escolher o arquivo (ex.: Fiat Strada 2024).");
      return;
    }
    setError(null);
    inputRef.current?.click();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(glb|zip)$/i.test(file.name)) {
      setError("Envie um arquivo .glb (ou o .zip do glTF baixado do Sketchfab).");
      return;
    }
    setError(null);
    try {
      await upload.mutateAsync({ name: name.trim(), notes: notes.trim(), file });
      setName("");
      setNotes("");
      toast.success("Modelo enviado");
    } catch (err) {
      setError(err instanceof PlatformApiError ? err.message : "Falha ao enviar o arquivo");
    }
  };

  const download = async (m: IconModelEntry) => {
    setDownloadingId(m.id);
    try {
      const blob = await gpsDriveIconModelsService.download(m.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = m.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao baixar o arquivo");
    } finally {
      setDownloadingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao remover");
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Modelos 3D</h1>
        <p className="text-sm text-muted-foreground">
          Envie aqui o modelo 3D (.glb) do veículo que vai virar ícone no mapa. Depois é só pedir para criar o ícone com o nome do modelo.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Novo modelo</CardTitle>
          <CardDescription>
            Arquivo .glb ou o .zip do glTF (como o Sketchfab baixa), até 150 MB. Nas observações, diga a cor/peça que deve pintar, se é carro, moto, caminhão…
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome do modelo (ex.: Fiat Strada 2024)" disabled={upload.isPending} />
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observações (opcional)"
            rows={2}
            disabled={upload.isPending}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <Button type="button" onClick={pick} disabled={upload.isPending}>
            <Upload className="h-3.5 w-3.5" />
            {upload.isPending ? "Enviando…" : "Escolher arquivo e enviar"}
          </Button>
          <input ref={inputRef} type="file" accept=".glb,.zip,model/gltf-binary,application/zip" className="hidden" onChange={onFile} />
          {error && <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">{error}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Modelos enviados</CardTitle>
          <CardDescription>{isLoading ? "Carregando…" : `${models?.length ?? 0} modelo(s)`}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {!isLoading && (models?.length ?? 0) === 0 && <p className="text-sm text-muted-foreground">Nenhum modelo enviado ainda.</p>}
          {models?.map((m) => (
            <div key={m.id} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted">
                <Box className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{m.name}</div>
                <p className="truncate text-xs text-muted-foreground">
                  {m.fileName} · {formatFileSize(m.fileSize)} · {new Date(m.createdAt).toLocaleString("pt-BR")}
                </p>
                {m.notes && <p className="text-xs text-muted-foreground">{m.notes}</p>}
              </div>
              <Button type="button" variant="outline" size="sm" className="shrink-0" disabled={downloadingId === m.id} onClick={() => download(m)}>
                <Download className="h-3.5 w-3.5" />
                {downloadingId === m.id ? "Baixando…" : "Baixar"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 text-red-600 hover:text-red-600"
                onClick={() => setPendingDelete(m)}
                disabled={remove.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover este modelo?</AlertDialogTitle>
            <AlertDialogDescription>O arquivo “{pendingDelete?.fileName}” será apagado. Ícones já criados com ele continuam funcionando.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
